/**
 * Path to Performance Data Factory Utilities
 * Factory functions to create initial/default data structures
 */

import type {
  PerformanceData,
  ActionItemData,
  AssessmentData,
} from "../../types/pathToPerformance";
import { 
  DEFAULT_ACTION_OPTION, 
  HUDDLES_OPTIONS,
  CATEGORY_NAMES,
} from "../../constants/pathToPerformance";

/**
 * Assessment periods (using individual constants instead of array for clarity)
 */
const ASSESSMENT_PERIOD_CURRENT = "Current";
const ASSESSMENT_PERIOD_PREVIOUS = "Previous";

/**
 * Create a default performance data object
 * 
 * @returns Default performance data
 */
export const createDefaultPerformanceData = (): PerformanceData => ({
  id: "1",
  ubtName: "",
  serviceType: "",
  usingHuddles: HUDDLES_OPTIONS.NO,
  dateTeamEstablished: "",
  dateTeamClosed: "",
  lastFaceToFaceAssessment: "",
  teamRating: 0,
});

/**
 * Create a default action item
 * 
 * @param id - Unique identifier
 * @param category - Category name
 * @returns Default action item
 */
export const createDefaultActionItem = (
  id: number,
  category: string,
): ActionItemData => ({
  id: String(id),
  category,
  categoryLevel: 0,
  actionNeeded: DEFAULT_ACTION_OPTION,
  notes: "",
});

/**
 * Create all default action items
 * 
 * @returns Array of default action items for all categories
 */
export const createDefaultActionItems = (): ActionItemData[] => {
  return CATEGORY_NAMES.map((category, index) =>
    createDefaultActionItem(index + 1, category),
  );
};

/**
 * Create a default assessment
 * 
 * @param id - Unique identifier
 * @param period - Assessment period (Current/Previous)
 * @returns Default assessment
 */
export const createDefaultAssessment = (
  id: number,
  period: string,
): AssessmentData => ({
  id: String(id),
  period,
  teamRating: 0,
  assessmentDate: "",
  assessmentNotes: "",
});

/**
 * Create all default assessments
 * 
 * @returns Array of default assessments (Current and Previous)
 */
export const createDefaultAssessments = (): AssessmentData[] => {
  return [
    createDefaultAssessment(1, ASSESSMENT_PERIOD_CURRENT),
    createDefaultAssessment(2, ASSESSMENT_PERIOD_PREVIOUS),
  ];
};

/**
 * Map huddles value from API format (Y/N) to display format (Yes/No)
 * 
 * @param apiValue - Value from API ("Y" or "N")
 * @returns Display value ("Yes" or "No")
 */
export const mapHuddlesFromApi = (apiValue: string): string => {
  return apiValue === "Y" ? HUDDLES_OPTIONS.YES : HUDDLES_OPTIONS.NO;
};

/**
 * Map huddles value from display format (Yes/No) to API format (Y/N)
 * 
 * @param displayValue - Display value ("Yes" or "No")
 * @returns API value ("Y" or "N")
 */
export const mapHuddlesToApi = (displayValue: string): "Y" | "N" => {
  return displayValue === HUDDLES_OPTIONS.YES ? "Y" : "N";
};
